import type { ReactNode, SelectHTMLAttributes, InputHTMLAttributes, TextareaHTMLAttributes } from 'react';
import { ChevronDown } from 'lucide-react';

// Hand-rolled primitives for the /tools pages. No component library: 1px
// rules, square corners, forest text on cream, brass only as a non-text accent.
// Every tool renders inside ToolPage's container, so widths are relative.

/** A titled block on the tool page (h2 + content). */
export const ToolSection = ({
  id,
  title,
  children,
  className = '',
}: {
  id: string;
  title: string;
  children: ReactNode;
  className?: string;
}) => (
  <section aria-labelledby={`${id}-heading`} className={`mt-14 ${className}`}>
    <h2 id={`${id}-heading`} className="text-subhead">
      {title}
    </h2>
    <div className="mt-5">{children}</div>
  </section>
);

/** The one-paragraph "what this tells you" block that opens every tool. */
export const ToolIntro = ({ children }: { children: ReactNode }) => (
  <section aria-labelledby="intro-heading" className="max-w-[46rem]">
    <h2 id="intro-heading" className="label">
      What this tells you
    </h2>
    <div className="mt-3 text-body [&>p+p]:mt-4">{children}</div>
  </section>
);

/** 1px-ruled panel on cream-light. */
export const Panel = ({ children, className = '' }: { children: ReactNode; className?: string }) => (
  <div className={`border border-forest bg-cream-light p-6 md:p-7 ${className}`}>{children}</div>
);

/** Label + hint wrapper around any control. */
export const Field = ({
  label,
  htmlFor,
  hint,
  children,
}: {
  label: string;
  htmlFor: string;
  hint?: string;
  children: ReactNode;
}) => (
  <div>
    <label htmlFor={htmlFor} className="field-label">
      {label}
    </label>
    {children}
    {hint && <p className="mt-2 text-[1rem] leading-[1.4] text-forest/80">{hint}</p>}
  </div>
);

export const NumberInput = (props: InputHTMLAttributes<HTMLInputElement>) => (
  <input type="number" inputMode="numeric" className="field" {...props} />
);

export const TextArea = (props: TextareaHTMLAttributes<HTMLTextAreaElement>) => (
  <textarea className="field min-h-[12rem] font-mono text-[1rem] leading-[1.5]" spellCheck={false} {...props} />
);

export const Select = ({ children, ...props }: SelectHTMLAttributes<HTMLSelectElement>) => (
  // WebKit's native appearance lets long option text overflow the field even
  // when its visible box fits. Keep the native control with a decorative arrow.
  <div className="relative min-w-0">
    <select className="field min-w-0 max-w-full appearance-none overflow-hidden text-ellipsis pr-10" {...props}>
      {children}
    </select>
    <ChevronDown aria-hidden="true" focusable="false" className="pointer-events-none absolute right-3 top-1/2 h-5 w-5 -translate-y-1/2 text-forest" />
  </div>
);

export interface ChoiceOption<T extends string> {
  value: T;
  label: string;
}

/** Radio group rendered as a row of square toggles; keyboard-operable via native radios. */
export const Choice = <T extends string>({
  name,
  legend,
  value,
  options,
  onChange,
}: {
  name: string;
  legend: ReactNode;
  value: T | null;
  options: ChoiceOption<T>[];
  onChange: (v: T) => void;
}) => (
  <fieldset className="border-b border-rule py-5">
    <legend className="text-body">{legend}</legend>
    <div className="mt-3 flex flex-wrap gap-2">
      {options.map((o) => {
        const id = `${name}-${o.value}`;
        const active = value === o.value;
        return (
          <label
            key={o.value}
            htmlFor={id}
            className={`cursor-pointer rounded-btn border px-4 py-2 text-small leading-none focus-within:outline focus-within:outline-2 focus-within:outline-offset-4 focus-within:outline-forest ${
              active ? 'border-forest bg-forest text-cream-light' : 'border-forest/50 bg-cream-light hover:border-forest'
            }`}
          >
            <input
              id={id}
              type="radio"
              name={name}
              value={o.value}
              checked={active}
              onChange={() => onChange(o.value)}
              className="sr-only"
            />
            {o.label}
          </label>
        );
      })}
    </div>
  </fieldset>
);

/** Table shell: horizontal scroll on narrow screens, 1px rules. */
export const Table = ({ children, caption }: { children: ReactNode; caption?: string }) => (
  // Keep the absolutely positioned accessible caption inside the scroll region.
  <div className="relative min-w-0 max-w-full overflow-x-auto border border-forest focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-forest" role="region" aria-label={caption ?? "Scrollable data table"} tabIndex={0}>
    <table className="w-full min-w-[40rem] border-collapse text-[1.0625rem] leading-[1.45]">
      {caption && <caption className="sr-only">{caption}</caption>}
      {children}
    </table>
  </div>
);

export const TH = ({ children, right = false }: { children: ReactNode; right?: boolean }) => (
  <th
    scope="col"
    className={`border-b border-forest bg-cream-light px-3 py-3 align-bottom text-[0.875rem] font-bold uppercase tracking-label ${
      right ? 'text-right' : 'text-left'
    }`}
  >
    {children}
  </th>
);

export const TD = ({
  children,
  right = false,
  strong = false,
  nowrap = false,
}: {
  children: ReactNode;
  right?: boolean;
  strong?: boolean;
  nowrap?: boolean;
}) => (
  <td
    className={`border-b border-rule px-3 py-3 align-top ${right ? 'text-right' : ''} ${strong ? 'font-bold' : ''} ${
      nowrap ? 'whitespace-nowrap' : ''
    }`}
  >
    {children}
  </td>
);

/** One statistic in a row of results. */
export const Stat = ({ label, value, note }: { label: string; value: ReactNode; note?: string }) => (
  <div className="border-t-2 border-forest pt-3">
    <p className="label">{label}</p>
    <p className="mt-2 text-[1.75rem] font-bold leading-[1.2]">{value}</p>
    {note && <p className="mt-1 text-[1rem] leading-[1.4] text-forest/80">{note}</p>}
  </div>
);

/** Numbered "how to read the result" list. */
export const ReadingList = ({ items }: { items: { title: string; body: ReactNode }[] }) => (
  <ol className="border-t border-rule">
    {items.map((i, idx) => (
      <li key={i.title} className="grid gap-2 border-b border-rule py-5 md:grid-cols-[3rem_1fr]">
        <span aria-hidden className="label">
          {String(idx + 1).padStart(2, '0')}
        </span>
        <div>
          <h3 className="text-small">{i.title}</h3>
          <div className="mt-1 text-body">{i.body}</div>
        </div>
      </li>
    ))}
  </ol>
);

export const formatUSD = (n: number) =>
  n.toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 });
